/** How consistently a driver takes each part of the track, across laps. */

import { required } from './columns';
import { axisGrid, interp } from './interp';
import { DEFAULT_STEP_M } from './resample';
import { roundTo } from './round';
import type { Deviation, Samples } from './types';

/** Median speed and standard deviation across laps, by distance.
 *
 * High deviation at a distance = inconsistent driving there.
 */
export function speedDeviation(laps: readonly Samples[], step: number = DEFAULT_STEP_M): Deviation {
  const usable = laps.filter((lap) => required(lap, 'dist').length > 0);
  if (usable.length < 2) return { dist: [], median: [], deviation: [] };
  let shortest = Infinity;
  const dists = usable.map((lap) => required(lap, 'dist'));
  const speeds = usable.map((lap) => required(lap, 'speed'));
  for (const d of dists) shortest = Math.min(shortest, d[d.length - 1]!);
  const grid = axisGrid(shortest, step);
  const n = usable.length;
  const median = new Array<number>(grid.length);
  const deviation = new Array<number>(grid.length);
  const here = new Float64Array(n);
  for (let g = 0; g < grid.length; g++) {
    const d = grid[g]!;
    for (let k = 0; k < n; k++) here[k] = interp(dists[k]!, speeds[k]!, d);
    here.sort();
    const mid = Math.floor(n / 2);
    const med = n % 2 ? here[mid]! : (here[mid - 1]! + here[mid]!) / 2;
    // Summed in sorted order, as the Python does.
    let sum = 0;
    for (let k = 0; k < n; k++) sum += here[k]!;
    const mean = sum / n;
    let sq = 0;
    for (let k = 0; k < n; k++) sq += (here[k]! - mean) ** 2;
    const variance = sq / n;
    median[g] = roundTo(med, 2);
    deviation[g] = roundTo(variance ** 0.5, 3);
  }
  return { dist: grid, median, deviation };
}
